
import { useState } from 'react';

interface BarkMeterProps {
  rating: number;
  maxRating?: number;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  interactive?: boolean;
  showLabel?: boolean;
  showExplainer?: boolean;
  onChange?: (rating: number) => void;
  variant?: 'default' | 'card' | 'inline';
}

const SIZE_MAP = {
  xs: { paw: 'text-sm', gap: 'gap-0.5', label: 'text-xs' },
  sm: { paw: 'text-base', gap: 'gap-1', label: 'text-xs' },
  md: { paw: 'text-xl', gap: 'gap-1', label: 'text-sm' },
  lg: { paw: 'text-2xl', gap: 'gap-1.5', label: 'text-base' },
  xl: { paw: 'text-4xl', gap: 'gap-2', label: 'text-lg' },
};

const BARK_LABELS: Record<number, { label: string; color: string; emoji: string }> = {
  1: { label: 'One Whimper', color: 'text-red-500', emoji: '😬' },
  2: { label: 'Tail Between Legs', color: 'text-orange-500', emoji: '😕' },
  3: { label: 'Cautious Sniff', color: 'text-yellow-500', emoji: '🐾' },
  4: { label: 'Happy Tail Wag', color: 'text-teal-500', emoji: '😄' },
  5: { label: 'Full Zoomies Mode', color: 'text-teal-600', emoji: '🐕' },
};

/**
 * PawIcon — renders a single filled or outline paw print using SVG path.
 * We use inline SVG since FontAwesome/Remix don't have a paw icon.
 */
function PawIcon({ filled, size }: { filled: boolean; size: string }) {
  return (
    <span
      className={`${size} transition-all duration-200 select-none`}
      style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
      aria-hidden="true"
    >
      {filled ? '🐾' : <span style={{ opacity: 0.25, filter: 'grayscale(1)' }}>🐾</span>}
    </span>
  );
}

export default function BarkMeter({
  rating,
  maxRating = 5,
  size = 'md',
  interactive = false,
  showLabel = false,
  showExplainer = false,
  onChange,
  variant = 'default',
}: BarkMeterProps) {
  const [hoverRating, setHoverRating] = useState(0);
  const sizes = SIZE_MAP[size];
  const activeRating = hoverRating || rating;
  const labelData = BARK_LABELS[Math.round(activeRating)] || BARK_LABELS[5];

  // Guard against invalid maxRating values
  const safeMaxRating = Number.isInteger(maxRating) && maxRating > 0 ? maxRating : 5;

  return (
    <div className="flex flex-col gap-1">
      <div className={`flex items-center ${sizes.gap}`}>
        {[...Array(safeMaxRating)].map((_, i) => {
          const starVal = i + 1;
          const filled = starVal <= activeRating;

          if (interactive) {
            return (
              <button
                key={i}
                type="button"
                onClick={() => onChange?.(starVal)}
                onMouseEnter={() => setHoverRating(starVal)}
                onMouseLeave={() => setHoverRating(0)}
                className={`cursor-pointer transition-transform hover:scale-125 focus:outline-none ${sizes.paw}`}
                aria-label={`Rate ${starVal} paw${starVal !== 1 ? 's' : ''}`}
              >
                <PawIcon filled={filled} size={sizes.paw} />
              </button>
            );
          }

          return (
            <span key={i} className={sizes.paw}>
              <PawIcon filled={filled} size={sizes.paw} />
            </span>
          );
        })}

        {showLabel && activeRating > 0 && (
          <span className={`ml-1.5 font-semibold ${labelData.color} ${sizes.label} whitespace-nowrap`}>
            {labelData.emoji} {labelData.label}
          </span>
        )}

        {variant === 'inline' && (
          <span className={`ml-1 text-gray-500 ${sizes.label}`}>({rating.toFixed(1)})</span>
        )}
      </div>

      {showExplainer && (
        <p className={`text-gray-400 italic ${sizes.label}`}>
          We rate in paws, not stars — because our customers deserve better 🐾
        </p>
      )}
    </div>
  );
}

/**
 * BarkMeterSummary — compact aggregate display (e.g. "4.9 🐾🐾🐾🐾🐾 (127 reviews)")
 */
export function BarkMeterSummary({
  rating,
  reviewCount,
  size = 'sm',
}: {
  rating: number;
  reviewCount: number;
  size?: 'xs' | 'sm' | 'md';
}) {
  const sizes = SIZE_MAP[size];
  return (
    <div className={`flex items-center ${sizes.gap} flex-wrap`}>
      <span className={`font-bold text-gray-900 ${sizes.label}`}>{rating.toFixed(1)}</span>
      <BarkMeter rating={rating} size={size} />
      <span className={`text-gray-500 ${sizes.label}`}>({reviewCount.toLocaleString()} reviews)</span>
    </div>
  );
}
